import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import api from '../../api/api';
import InvoiceOrderLineTree from '../InvoiceOrderLineTree';
import DateFilterDropdown from '../DateFilterDropdown';
import InvoiceSearchFilter from '../InvoiceSearchFilter';
import { useTheme } from '../../context/ThemeContext';

interface InvoiceGroup {
  orderLineId: number | null;
  sessionCode: string;
  status: string;
  startedAt?: string;
  endedAt?: string;
  totalAmount?: number;
  paidAmount?: number;
  customerType?: string;
  invoicesTotal: number;
  customer?: {
    id?: number;
    username: string;
    email?: string;
    phone?: string;
    type: string;
  };
  table?: {
    id: number;
    name: string;
  };
  invoices: any[];
  invoiceCount: number;
}

interface OtherOrdersTabProps {
  vendorId: string;
  initialDateFilter?: string;
  onDateFilterChange?: (filter: string) => void;
}

/**
 * OtherOrdersTab - Shows non-table orders (takeaway, delivery, counter, etc.)
 * This is the refactored version of the original VendorInvoices content.
 */
const OtherOrdersTab: React.FC<OtherOrdersTabProps> = ({
  vendorId,
  initialDateFilter = 'today',
  onDateFilterChange
}) => {
  const [invoiceGroups, setInvoiceGroups] = useState<InvoiceGroup[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [dateFilter, setDateFilter] = useState<string>(initialDateFilter);
  const [page, setPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchCriteria, setSearchCriteria] = useState<string>('none');
  const debounceTimerRef = useRef<NodeJS.Timeout>();
  const { theme } = useTheme();
  const { components: { card }, gray, primary, text, button } = theme;

  // Reset page to 0 when filters change
  useEffect(() => {
    setPage(0);
  }, [dateFilter, searchQuery, searchCriteria]);

  // Handle fetching with debouncing for search
  useEffect(() => {
    if (!vendorId) {
      setError("Vendor ID is missing. Cannot fetch invoices.");
      setLoading(false);
      return;
    }

    // Clear any pending debounced call
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Check if search is active (criteria selected and query provided)
    const hasActiveSearch = searchCriteria !== 'none' && searchQuery.trim().length > 0;

    if (hasActiveSearch) {
      // Debounce search queries
      debounceTimerRef.current = setTimeout(() => {
        fetchInvoices();
      }, 300);
    } else {
      // Immediate fetch for non-search changes or when search is cleared
      fetchInvoices();
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [vendorId, dateFilter, page, searchQuery, searchCriteria]);

  // Notify parent of date filter changes
  useEffect(() => {
    onDateFilterChange?.(dateFilter);
  }, [dateFilter, onDateFilterChange]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      // Build query parameters
      const params = new URLSearchParams({
        groupByOrderLine: 'true',
        dateFilter: dateFilter,
        page: page.toString(),
        size: '20'
      });

      // Add search parameters only if a valid search criteria is selected and query is provided
      if (searchCriteria !== 'none' && searchQuery && searchQuery.trim()) {
        params.append('searchQuery', searchQuery.trim());
        params.append('searchCriteria', searchCriteria);
      }

      const apiUrl = `/vendors/${vendorId}/invoices?${params.toString()}`;
      const response = await api.get(apiUrl);

      let data = response.data;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch {
          setError('Invalid JSON response from server.');
          setInvoiceGroups([]);
          return;
        }
      }

      if (data && typeof data === 'object' && 'content' in data) {
        setInvoiceGroups(data.content || []);
        setTotalPages(data.totalPages || 0);
        setTotalElements(data.totalElements || 0);
      } else if (Array.isArray(data)) {
        setInvoiceGroups(data);
        setTotalPages(1);
        setTotalElements(data.length);
      } else {
        setError('Invalid response format from server.');
        setInvoiceGroups([]);
        return;
      }
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch vendor invoices.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchClear = () => {
    setSearchQuery('');
    setSearchCriteria('none');
  };

  const handleCriteriaChange = (criteria: string) => {
    setSearchCriteria(criteria);
    if (criteria === 'none') {
      setSearchQuery('');
    }
  };

  const handleDateFilterChange = (value: string) => {
    setDateFilter(value);
  };

  const renderContent = () => {
    if (loading) {
      return <p className="text-center py-8" style={{ color: gray[500] }}>Loading invoices...</p>;
    }

    if (error) {
      return (
        <p
          className="p-4 border rounded-md text-center"
          style={{
            backgroundColor: button.danger.bg,
            color: button.danger.text,
            borderColor: button.danger.bg,
          }}
        >
          {error}
        </p>
      );
    }

    if (invoiceGroups.length === 0) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No invoices found for the selected filters.
          </Typography>
        </Box>
      );
    }

    return <InvoiceOrderLineTree groups={invoiceGroups as any} theme={theme as any} loading={loading} />;
  };

  const renderFilters = () => {
    return (
      <div className="flex flex-col gap-2 mb-4">
        <div className="flex flex-row items-center gap-2">
          <DateFilterDropdown
            value={dateFilter}
            onChange={handleDateFilterChange}
            showAllOption={true}
          />
        </div>
        <InvoiceSearchFilter
          searchQuery={searchQuery}
          searchCriteria={searchCriteria}
          onSearchChange={setSearchQuery}
          onCriteriaChange={handleCriteriaChange}
          onClear={handleSearchClear}
        />
      </div>
    );
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          mt: 4,
          mb: 2,
          px: 2
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: gray[600],
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            mb: 1
          }}
        >
          Showing {page * 20 + 1}-{Math.min((page + 1) * 20, totalElements)} of {totalElements} invoices
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
          {page > 0 && (
            <button
              onClick={() => setPage(page - 1)}
              style={{
                padding: '6px 12px',
                fontSize: '0.875rem',
                borderRadius: '6px',
                border: `1px solid ${gray[300]}`,
                backgroundColor: card.background,
                color: text,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              Previous
            </button>
          )}

          {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i;
            } else if (page < 3) {
              pageNum = i;
            } else if (page >= totalPages - 3) {
              pageNum = totalPages - 5 + i;
            } else {
              pageNum = page - 2 + i;
            }

            return (
              <button
                key={pageNum}
                onClick={() => setPage(pageNum)}
                style={{
                  minWidth: '40px',
                  height: '40px',
                  fontSize: '0.875rem',
                  fontWeight: page === pageNum ? 600 : 400,
                  borderRadius: '6px',
                  border: `1px solid ${page === pageNum ? primary : gray[300]}`,
                  backgroundColor: page === pageNum ? primary : card.background,
                  color: page === pageNum ? '#fff' : text,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                {pageNum + 1}
              </button>
            );
          })}

          {page < totalPages - 1 && (
            <button
              onClick={() => setPage(page + 1)}
              style={{
                padding: '6px 12px',
                fontSize: '0.875rem',
                borderRadius: '6px',
                border: `1px solid ${gray[300]}`,
                backgroundColor: card.background,
                color: text,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              Next
            </button>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      {renderFilters()}
      {renderContent()}
      {renderPagination()}
    </Box>
  );
};

export default OtherOrdersTab;
